import { map, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

/**
 * interfaces
 */
import { AppResponse, IAppResponseModel, IResponse } from '@network';
import {
    IUnitDto,
    IUnitChildDto,
    IUnitDetailsDto,
    IUnitDropdownDto,
    IUnitTreeDto,
    IUnitListItemDto,
    ICreateUnitPayload,
    IUpdateUnitPayload,
} from '../../interfaces';

/**
 * constants
 */
import { UnitsConst } from '../../consts';

/**
 * HTTP layer for the Units API endpoints.
 * Makes raw HTTP requests and returns typed IResponse wrappers.
 * Contains no business logic.
 */
@Injectable()
export class UnitApiService {

    /**
     * @param _http - Angular HTTP client.
     */
    constructor(private readonly _http: HttpClient) {}

    /**
     * Fetches the first-level units (direct children of the root).
     * @returns Observable emitting an array of first-level unit DTOs.
     */
    public getFirstLevel(): Observable<IResponse<IUnitDto[]>> {
        return this._http.get<IResponse<IUnitDto[]>>(UnitsConst.FIRST_LEVEL_ENDPOINT);
    }

    /**
     * Fetches the direct children of a given unit (lazy tree load).
     * @param parentId - ID of the parent unit whose children to load.
     * @returns Observable emitting an array of child unit DTOs.
     */
    public getChildren(parentId: string): Observable<IResponse<IUnitChildDto[]>> {
        return this._http.get<IResponse<IUnitChildDto[]>>(UnitsConst.GET_CHILDREN_ENDPOINT(parentId));
    }

    /**
     * Fetches the full detail of a single unit by its ID.
     * @param id - Unit ID.
     * @returns Observable emitting the full unit detail DTO.
     */
    public getById(id: string): Observable<IResponse<IUnitDetailsDto>> {
        return this._http.get<IResponse<IUnitDetailsDto>>(UnitsConst.GET_BY_ID_ENDPOINT(id));
    }

    /**
     * Creates a new unit.
     * @param payload - Unit creation data.
     * @returns Observable emitting a success message string.
     */
    public create(payload: ICreateUnitPayload): Observable<IResponse<string>> {
        return this._http.post<IResponse<string>>(UnitsConst.CREATE_ENDPOINT, payload);
    }

    /**
     * Updates an existing unit by its ID.
     * @param id - ID of the unit to update.
     * @param payload - Updated unit data.
     * @returns Observable emitting a success message string.
     */
    public update(id: string, payload: IUpdateUnitPayload): Observable<IResponse<string>> {
        return this._http.put<IResponse<string>>(UnitsConst.UPDATE_ENDPOINT(id), payload);
    }

    /**
     * Deletes a unit by its ID.
     * @param id - ID of the unit to delete.
     * @returns Observable emitting a success message string.
     */
    public delete(id: string): Observable<IResponse<string>> {
        return this._http.delete<IResponse<string>>(UnitsConst.DELETE_ENDPOINT(id));
    }

    /**
     * Fetches the flat dropdown list of all units (facility + buildings + sub-units).
     * Used by the building resolver to populate the navbar unit selector.
     * @returns Observable emitting an array of unit dropdown DTOs.
     */
    public getDropdown(): Observable<IResponse<IUnitDropdownDto[]>> {
        return this._http.get<IResponse<IUnitDropdownDto[]>>(UnitsConst.DROPDOWN_ENDPOINT);
    }

    /**
     * Fetches the hierarchical unit tree rooted at the given unit ID.
     * Shared lookup — reused by Energy Settings (Grid Billing Account,
     * Energy Meter Mappings) in addition to Units Management.
     * @param id - ID of the unit to root the tree at.
     * @returns Observable emitting an array of root-level unit tree node DTOs.
     */
    public getTree(id: string): Observable<IResponse<IUnitTreeDto[]>> {
        return this._http.get<IResponse<IUnitTreeDto[]>>(UnitsConst.TREE_ENDPOINT(id));
    }

    /**
     * Fetches every unit below a building as a flat list, at any depth; the
     * building itself is not in it.
     * @param buildingId - ID of the building whose units to list.
     * @returns Observable emitting the wrapped list of unit DTOs.
     */
    public getList(buildingId: string): Observable<IAppResponseModel<IUnitListItemDto[]>> {
        return this._http
            .get<IResponse<IUnitListItemDto[]>>(UnitsConst.LIST_ENDPOINT(buildingId), { observe: 'response' })
            .pipe(map((response) => new AppResponse(response)));
    }

}
